import { useState, useEffect } from "react";
import { Row, Col, Table, Input, Form, Modal, DatePicker, Select } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { CALL_FEATCH_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";
import COLORS from "common/Colors";
import dayjs from "dayjs";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import { ReactComponent as FilterIcon } from "../../assets/filter-svgrepo-com (2).svg";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import { kenyaFormatDateTime } from "utils/Date";
import { currencyCodeMap } from "common/Constants";

// ─── Styled Components ─────────────────────────────────────────────────────

const PageWrap = styled.div`
  min-height: 100vh;
  padding: 28px 0px;
`;

const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
  .custom-filter-btn{
	margin-bottom: 0!important;
  }
`;

const TotalLog = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.TEXT?.PRIMARY || "#111827"};
  white-space: nowrap;
`;

const FiltersGroup = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const DropdownWrapper = styled.div`
  width: 160px;
  display: flex;
`;


const FilterBtn = styled.div`
  position: relative;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d9d9d9;
  border-radius: 20px;
  background: #deefee;
  cursor: pointer;
  color: #6b7280;
  flex-shrink: 0;
`;

const FilterBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #fff;
`;

const SearchInput = styled(Input)`
  && {
	width: 160px;
	border-radius: 20px !important;
	font-size: 13px;
	&:hover, &:focus { 
	  border-color: #2ecc71 !important; 
	  box-shadow: none !important; 
	}
  }
`;

// ─── Table Styles ──────────────────────────────────────────────────────────

const StyledTable = styled(Table)`
  padding-top: 10px;
  table {
	width: 100% !important;
	border: 1px solid #ccc;
  }
  .ant-table-content {
	overflow-x: auto;
	white-space: nowrap;
	color: ${COLORS.TEXT.PRIMARY};
	font-size: 14px;
  }
  .ant-table-row:nth-child(even) {
	background-color: #fafafa;
  }
  .ant-table-thead .ant-table-cell {
	font-weight: bold;
	color: ${COLORS.TEXT.PRIMARY};
	white-space: nowrap;
	padding: 12px 16px !important;
	height: 48px !important;
  }
  .ant-table-tbody .ant-table-cell {
	padding: 12px 16px !important;
	height: 48px !important;
  }
`;

const ActionByCell = styled.div`
  .name {
	font-weight: 500;
	font-size: 13px;
	color: #111827;
  }
  .time {
	font-size: 12px;
	color: #9ca3af;
	margin-top: 2px;
	font-style: italic;
  }
`;

const LinkText = styled.a`
  font-size: 14px;
  color: #2196f3;
  text-decoration: none;
  cursor: pointer;
  &:hover {
	text-decoration: underline;
  }
`;

const MoneyText = styled.span`
  font-size: 14px;
  color: ${({ $muted }) => ($muted ? "#9ca3af" : "#111827")};
  font-weight: ${({ $muted }) => ($muted ? 400 : 500)};
`;

// ─── Detail Modal Styles ───────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 580px;
  padding: 32px 36px 36px;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
`;

const ModalCloseBtn = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 18px;
  padding: 4px;
  border-radius: 4px;
  &:hover {
	color: #374151;
	background: #f3f4f6;
  }
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 24px 0;
`;

const ModalField = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 11px 0;
  border-bottom: none;
`;

const FieldLabel = styled.div`
  width: 190px;
  flex-shrink: 0;
  font-size: 14px;
  color: #6b7280;
  font-weight: 400;
`;

const FieldValue = styled.div`
  font-size: 14px;
  color: #111827;
  font-weight: 400;
  flex: 1;
`;

const MetaBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 400;
  background: ${({ $variant }) => $variant === "approved" ? "#e6f4f0" : "#f0f0f0"};
  color: ${({ $variant }) => $variant === "approved" ? "#15803d" : "#374151"};
`;

const MetaBadgeRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const DescriptionBlock = styled.div`
  padding: 8px 0 4px;
  margin-top: 4px;
`;

const DescriptionLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
  font-weight: 400;
`;

const DescriptionText = styled.div`
  font-size: 14px;
  color: #111827;
  line-height: 1.7;
  word-break: break-all;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
	border-radius: 20px !important;
	min-height: 36px !important;
	height: auto !important;
	align-items: center;
	display: flex !important;
	font-size: 13px;
	flex-wrap: wrap;
	padding: 2px 8px !important;
  }
  &.ant-select-focused .ant-select-selector,
  &:hover .ant-select-selector {
	border-color: #E2E2E8 !important;
	box-shadow: 0 0 0 2px rgba(82, 198, 176, 0.15) !important;
  }
`;

// ─── API ───────────────────────────────────────────────────────────────────

const API = {
	GET_TRANSACTIONS: "insyts-settings/transactions",
	GET_FILTER_LIST: "insyts-settings/transactionfilterlist",
};

const PAGE_SIZE = 10;

// ─── Helpers ───────────────────────────────────────────────────────────────

const fmt = (v) => (!v || v === "---" || v === "—" ? "———" : v);
const fmtDate = (iso) => (iso ? dayjs(iso).format("Do MMM YYYY") : "———");

const normaliseTransaction = (item, index) => ({
	key: item.transactionid || String(index),
	by: item.createdby || "—",
	time: kenyaFormatDateTime(item.createdat) || "—",
	matter: item.mattername || "—",
	matterid: item.matterid || null,
	category: item.category || "—",
	source: item.sourcename || "—",
	destination: item.destinationname || "—",
	fundOut: item.fundout != null ? `${item.currency || ""} ${item.fundout}`.trim() : "---",
	fundIn: item.fundin != null ? `${item.currency || ""} ${item.fundin}`.trim() : "---",
	balance: item.fundbalance != null ? `${item.currency || ""} ${item.fundbalance}`.trim() : "—",
	_raw: item,
});

// ─── Detail Modal Components ───────────────────────────────────────────────

const FundAddedModal = ({ raw }) => {
	const amount = raw.fundin ?? raw.fundout;
	return (
		<>
			<ModalTitle>Fund Added</ModalTitle>
			<ModalField><FieldLabel>Client</FieldLabel>              <FieldValue>{fmt(raw.clientname)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Matter</FieldLabel>              <FieldValue>{fmt(raw.mattername)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Date of Transaction</FieldLabel> <FieldValue>{fmtDate(raw.dateoftransaction)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Amount</FieldLabel>              <FieldValue>{amount != null ? `${raw.currency || ""} ${amount}`.trim() : "———"}</FieldValue></ModalField>
			<ModalField><FieldLabel>Source</FieldLabel>              <FieldValue>{fmt(raw.sourcename)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Destination</FieldLabel>         <FieldValue>{fmt(raw.destinationname)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Payment Method</FieldLabel>      <FieldValue>{fmt(raw.paymentmethod)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Other Remarks</FieldLabel>       <FieldValue>{fmt(raw.otherremarks)}</FieldValue></ModalField>
		</>
	);
};

function resolveNames(emailsOrIds, userList, currentUserId = "") {
	if (!Array.isArray(emailsOrIds) || !emailsOrIds.length) return "---";
	return emailsOrIds
		.map((val) => {
			if (!val) return "—";
			if (currentUserId && val?.toLowerCase() === currentUserId?.toLowerCase()) return "You";
			const user = userList?.find(
				(u) => u?.email === val || u?.userid === val || u?.emailid === val
			);
			return user?.fullname || user?.firstname || val;
		})
		.join(", ");
}

const FundRequestModal = ({ raw }) => {
	const amount = raw.fundin ?? raw.fundout;

	const formatMetaDate = (iso) =>
		iso ? dayjs(iso).format("Do MMM, YYYY") : null;

	const userList = useSelector(getUserList);
	const userInfo = useSelector(getUserInfo);

	return (
		<>
			<ModalTitle>
				Fund Request{" "}
				{raw.fundrequestid && (
					<span style={{ color: "#2196f3", fontSize: 16 }}>
						({raw.fundrequestid})
					</span>
				)}
			</ModalTitle>
			<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, justifyContent: "space-between" }}>

				{raw.createdby && (
					<MetaBadge>
						<span style={{ color: "#6b7280", fontWeight: "600" }}>Created by:</span>{" "}
						<span style={{ color: "#374151" }}>
							{resolveNames([raw.createdby], userList, userInfo?.userid)}
						</span>
						{raw.createdat && (
							<span style={{ color: "#6b7280" }}>
								; {formatMetaDate(raw.createdat)}
							</span>
						)}
					</MetaBadge>
				)}
				{raw.fundrequestapprovedby && (
					<MetaBadge $variant="approved">
						<span style={{ color: "#03d34f", fontWeight: "600" }}>Approved by:</span>{" "}
						<span style={{ color: "#15803d" }}>
							{resolveNames([raw.fundrequestapprovedby], userList, userInfo?.userid)}
						</span>
						{raw.fundrequestcreatedat && (
							<span style={{ color: "#15803d" }}>
								; {formatMetaDate(raw.fundrequestcreatedat)}
							</span>
						)}
					</MetaBadge>
				)}
			</div>

			<ModalField>
				<FieldLabel>Client</FieldLabel>
				<FieldValue>{fmt(raw.clientname)}</FieldValue>
			</ModalField>
			<ModalField>
				<FieldLabel>Requesting Fund for</FieldLabel>
				{raw?.fundrequesttype === "Matter Fund" ? (
					<FieldValue>{fmt(raw.fundrequesttype || raw.sourcename)}</FieldValue>
				) : <FieldValue>{fmt(raw.fundrequesttype || raw.sourcename)}</FieldValue>}
			</ModalField>
			{raw?.fundrequesttype === "Matter Fund" && (
				<ModalField>
					<FieldLabel>Matter Name</FieldLabel>
					<FieldValue>{fmt(raw.mattername)}</FieldValue>
				</ModalField>
			)}
			<ModalField>
				<FieldLabel>Amount</FieldLabel>
				<FieldValue>
					{amount != null ? `${raw.currency || ""} ${amount}`.trim() : "———"}
				</FieldValue>
			</ModalField>
			<ModalField>
				<FieldLabel>Due Date</FieldLabel>
				<FieldValue>{fmtDate(raw.duedate || raw.dateoftransaction)}</FieldValue>
			</ModalField>

			{/* Description — full-width block below the fields */}
			{raw.description && (
				<DescriptionBlock>
					<DescriptionLabel>Description</DescriptionLabel>
					<DescriptionText>{raw.description}</DescriptionText>
				</DescriptionBlock>
			)}
		</>
	);
};

const FundMovedModal = ({ raw }) => {
	const amount = raw.fundin ?? raw.fundout;
	return (
		<>
			<ModalTitle>Fund Moved</ModalTitle>
			<ModalField><FieldLabel>Client</FieldLabel>             <FieldValue>{fmt(raw.clientname)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Date of Movement</FieldLabel>   <FieldValue>{fmtDate(raw.dateoftransaction)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Source</FieldLabel>             <FieldValue>{fmt(raw.sourcename)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Destination</FieldLabel>        <FieldValue>{fmt(raw.destinationname)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Amount Transferred</FieldLabel> <FieldValue>{amount != null ? `${raw.currency || ""} ${amount}`.trim() : "———"}</FieldValue></ModalField>
		</>
	);
};

const TransactionDetailModal = ({ record, onClose }) => {
	if (!record) return null;
	const category = record.category?.trim();
	const renderContent = () => {
		if (category === "Fund Added") return <FundAddedModal raw={record._raw} />;
		if (category === "Fund Request") return <FundRequestModal raw={record._raw} />;
		if (category === "Fund Moved") return <FundMovedModal raw={record._raw} />;
		return (
			<>
				<ModalTitle>{category}</ModalTitle>
				<pre style={{ fontSize: 12 }}>{JSON.stringify(record._raw, null, 2)}</pre>
			</>
		);
	};
	return (
		<ModalOverlay onClick={onClose}>
			<ModalBox onClick={(e) => e.stopPropagation()}>
				<ModalCloseBtn onClick={onClose}><CloseOutlined /></ModalCloseBtn>
				{renderContent()}
			</ModalBox>
		</ModalOverlay>
	);
};

// ─── Main Component ────────────────────────────────────────────────────────

const Transaction = ({ clientId, clientData, module }) => {
	const userInfo = useSelector(getUserInfo);
	const userList = useSelector(getUserList);
	const resolvedClientId = clientData?.bdo_id || userInfo?.clientId || "";

	const [form] = Form.useForm();

	// top-bar filter state
	const [search, setSearch] = useState("");
	const [actionByFilter, setActionByFilter] = useState([]);
	const [matterFilter, setMatterFilter] = useState([]);
	const [categoryFilter, setCategoryFilter] = useState([]);

	// modal filter state
	const [filterModalOpen, setFilterModalOpen] = useState(false);
	const [modalSource, setModalSource] = useState([]);
	const [modalDest, setModalDest] = useState([]);
	const [modalPayment, setModalPayment] = useState("");
	const [modalCurrency, setModalCurrency] = useState([]);
	const [modalFundIn, setModalFundIn] = useState("");
	const [modalFundOut, setModalFundOut] = useState("");
	const [modalFundBalance, setModalFundBalance] = useState("");
	const [modalDateFrom, setModalDateFrom] = useState(null);
	const [modalDateTo, setModalDateTo] = useState(null);

	// filter options — populated from API response:
	//   createdby:      [{ userid }]          → actionBy:     [{ label, value }]
	//   matters:        [{ matterid, mattername }] → matters: [{ label: mattername, value: matterid }]
	//   categories:     string[]              → categories:   [{ label, value }]
	//   sources:        string[]              → sources:      string[]
	//   destinations:   string[]              → destinations: string[]
	//   paymentmethods: string[]              → paymentMethods: string[]
	//   currencies:     string[]              → currencies:   string[]
	const [filterOptions, setFilterOptions] = useState({
		actionBy: [],
		categories: [],
		matters: [],
		sources: [],
		destinations: [],
		paymentMethods: [],
		currencies: [],
	});
	const [loadingFilters, setLoadingFilters] = useState(false);

	// table state
	const [tableData, setTableData] = useState([]);
	const [loadingTable, setLoadingTable] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalRecords, setTotalRecords] = useState(0);

	// detail modal
	const [selectedRecord, setSelectedRecord] = useState(null);

	// Always returns a plain string — prevents toLowerCase crash in DropdownCheckBox
	const getUserName = (emailOrId) => {
		if (emailOrId == null) return "";
		const user = (userList || []).find(
			(u) => u.userid === emailOrId || u.email === emailOrId
		);
		return String(user?.fullname || emailOrId);
	};

	const currencyLabelMap = Object.entries(currencyCodeMap).reduce((acc, [name, code]) => {
		const label = name
			.replace(/_/g, " ")
			.replace(/\b\w/g, (c) => c.toUpperCase()); // "australian_dollar" → "Australian Dollar"
		acc[code] = label;
		return acc;
	}, {});

	// count active modal filters for the badge on the filter icon
	const modalFilterCount = [
		modalSource.length > 0,
		modalDest.length > 0,
		modalCurrency.length > 0,
		!!modalPayment,
		!!modalFundIn,
		!!modalFundOut,
		!!modalFundBalance,
		!!(modalDateFrom || modalDateTo),
	].filter(Boolean).length;

	// ── Fetch filter list ────────────────────────────────────────────────────
	const fetchFilterList = async () => {
		setLoadingFilters(true);
		try {
			const resp = await CALL_FEATCH_API(
				`${API.GET_FILTER_LIST}?clientid=${resolvedClientId}`,
				"GET"
			);

			if (resp?.response?.data) {
				const d = resp.response.data;

				// createdby: [{ userid }]
				const actionBy = Array.isArray(d.createdby)
					? d.createdby
						.filter((u) => u?.userid != null)
						.map((u) => ({
							label: String(getUserName(u.userid) || u.userid),
							value: String(u.userid),
						}))
					: [];

				// categories: string[]
				const categories = Array.isArray(d.categories)
					? d.categories
						.filter((c) => c != null)
						.map((c) => ({ label: String(c), value: String(c) }))
					: [];

				// matters: [{ matterid, mattername }]
				const matters = Array.isArray(d.matters)
					? d.matters
						.filter((m) => m?.matterid != null && m?.mattername != null)
						.map((m) => ({
							label: String(m.mattername),
							value: String(m.matterid),
						}))
					: [];

				// sources: string[]
				const sources = Array.isArray(d.sources)
					? d.sources.filter((s) => s != null).map((s) => String(s))
					: [];

				// destinations: string[]
				const destinations = Array.isArray(d.destinations)
					? d.destinations.filter((s) => s != null).map((s) => String(s))
					: [];

				// paymentmethods: string[]
				const paymentMethods = Array.isArray(d.paymentmethods)
					? d.paymentmethods.filter((s) => s != null).map((s) => String(s))
					: [];

				// currencies: string[]
				const currencies = Array.isArray(d.currencies)
					? d.currencies.filter((s) => s != null).map((s) => String(s))
					: [];

				setFilterOptions({
					actionBy,
					categories,
					matters,
					sources,
					destinations,
					paymentMethods,
					currencies,
				});
			}
		} catch (err) {
			console.error("fetchFilterList error:", err);
		} finally {
			setLoadingFilters(false);
		}
	};

	// ── Fetch transactions ───────────────────────────────────────────────────
	const fetchTransactions = async (page = 1, overrides = {}) => {
		setLoadingTable(true);
		try {
			const actionBy = "actionBy" in overrides ? overrides.actionBy : actionByFilter;
			const matter = "matter" in overrides ? overrides.matter : matterFilter;
			const category = "category" in overrides ? overrides.category : categoryFilter;
			const src = "source" in overrides ? overrides.source : modalSource;
			const dest = "dest" in overrides ? overrides.dest : modalDest;
			const payment = "payment" in overrides ? overrides.payment : modalPayment;
			const currency = "currency" in overrides ? overrides.currency : modalCurrency;
			const fundIn = "fundIn" in overrides ? overrides.fundIn : modalFundIn;
			const fundOut = "fundOut" in overrides ? overrides.fundOut : modalFundOut;
			const fundBal = "fundBal" in overrides ? overrides.fundBal : modalFundBalance;
			const dateFrom = "dateFrom" in overrides ? overrides.dateFrom : modalDateFrom;
			const dateTo = "dateTo" in overrides ? overrides.dateTo : modalDateTo;
			const srch = "search" in overrides ? overrides.search : search;

			const params = new URLSearchParams({
				clientid: resolvedClientId,
				page: String(page),
				pagesize: String(PAGE_SIZE),
			});

			if (srch) params.append("search", srch);
			if (actionBy?.length) params.append("actionby", actionBy.join(","));
			if (matter?.length) params.append("matterid", matter.join(","));   // sends matterid values
			if (category?.length) params.append("category", category.join(","));
			if (src?.length) params.append("source", src.join(","));
			if (dest?.length) params.append("destination", dest.join(","));
			if (payment) params.append("paymentmethod", payment);
			if (currency?.length) params.append("currency", currency.join(","));
			if (fundIn) params.append("fundin", fundIn);
			if (fundOut) params.append("fundout", fundOut);
			if (fundBal) params.append("fundbalance", fundBal);
			if (dateFrom) params.append("datefrom", dateFrom.clone().startOf("day").toISOString());
			if (dateTo) params.append("dateto", dateTo.clone().endOf("day").toISOString());

			const resp = await CALL_FEATCH_API(
				`${API.GET_TRANSACTIONS}?${params.toString()}`,
				"GET"
			);

			if (resp) {
				const rawList =
					resp.response?.data ||
					resp.data ||
					resp.records ||
					resp.transactions ||
					[];
				const total =
					resp.response?.pagination?.total ||
					resp.response?.total ||
					resp.total ||
					rawList.length;

				setTableData(Array.isArray(rawList?.filter(
				item => !(item.sourcename === "Direct Payment" && item.fundout)
				)) ? rawList?.filter(
				item => !(item.sourcename === "Direct Payment" && item.fundout)
				)?.map(normaliseTransaction) : []);
				setTotalRecords(total);
				setCurrentPage(page);
			}
		} catch (err) {
			console.error("fetchTransactions error:", err);
			setTableData([]);
		} finally {
			setLoadingTable(false);
		}
	};

	useEffect(() => { fetchFilterList(); }, []); // eslint-disable-line
	useEffect(() => { fetchTransactions(1); }, [resolvedClientId]); // eslint-disable-line

	// ── Top-bar filter change ────────────────────────────────────────────────
	const handleTopFilterChange = (_, allValues) => {
		const actionBy = allValues?.actionBy || [];
		const matter = allValues?.matter || [];
		const category = allValues?.category || [];
		setActionByFilter(actionBy);
		setMatterFilter(matter);
		setCategoryFilter(category);
		fetchTransactions(1, { actionBy, matter, category });
	};

	// ── Apply modal filters ──────────────────────────────────────────────────
	const applyModalFilters = () => {
		fetchTransactions(1, {
			source: modalSource,
			dest: modalDest,
			payment: modalPayment,
			currency: modalCurrency,
			fundIn: modalFundIn,
			fundOut: modalFundOut,
			fundBal: modalFundBalance,
			dateFrom: modalDateFrom,
			dateTo: modalDateTo,
		});
		setFilterModalOpen(false);
	};

	const clearModalFilters = () => {
		setModalSource([]);
		setModalDest([]);
		setModalPayment("");
		setModalCurrency([]);
		setModalFundIn("");
		setModalFundOut("");
		setModalFundBalance("");
		setModalDateFrom(null);
		setModalDateTo(null);
		fetchTransactions(1, {
			source: "", dest: "", payment: "", currency: "",
			fundIn: "", fundOut: "", fundBal: "", dateFrom: null, dateTo: null,
		});
		setFilterModalOpen(false);
	};

	// ── Columns ──────────────────────────────────────────────────────────────
	const columns = [
		{
			title: "Action by",
			key: "by",
			width: 180,
			render: (_, r) => (
				<ActionByCell>
					<div className="name">{getUserName(r.by)}</div>
					<div className="time">{r.time}</div>
				</ActionByCell>
			),
		},
		{
			title: "Matter",
			key: "matter",
			width: 180,
			render: (_, r) => (
				<ActionByCell>
					<div className="name">{r.matter}</div>
					{r.matterid && <div className="time">{r.matterid}</div>}
				</ActionByCell>
			),
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			width: 210,
			render: (val, record) => (
				<ActionByCell>
					<LinkText onClick={() => setSelectedRecord(record)}>{val}</LinkText>
					{record._raw?.fundrequestid && <div className="time">{record._raw.fundrequestid}</div>}
				</ActionByCell>
			),
		},
		{
			title: "Source",
			dataIndex: "source",
			key: "source",
			width: 190,
			render: (val) => <span style={{ fontSize: 14, color: "#444" }}>{val}</span>,
		},
		{
			title: "Destination",
			dataIndex: "destination",
			key: "destination",
			width: 180,
			render: (val) => <span style={{ fontSize: 14, color: "#444" }}>{val}</span>,
		},
		{
			title: "Fund Out",
			dataIndex: "fundOut",
			key: "fundOut",
			width: 110,
			align: "right",
			render: (val) => <MoneyText $muted={val === "---"}>{val}</MoneyText>,
		},
		{
			title: "Fund In",
			dataIndex: "fundIn",
			key: "fundIn",
			width: 110,
			align: "right",
			render: (val) => <MoneyText $muted={val === "---"}>{val}</MoneyText>,
		},
		// {
		// 	title: "Fund Balance",
		// 	dataIndex: "balance",
		// 	key: "balance",
		// 	width: 130,
		// 	align: "right",
		// 	render: (val) => <MoneyText>{val}</MoneyText>,
		// },
	];

	// ── Shared inline styles ─────────────────────────────────────────────────
	const selectStyle = {
		width: "100%", height: 40, borderRadius: 20,
		border: "1px solid #d9d9d9", padding: "0 14px",
		fontSize: 14, color: "#374151", outline: "none", background: "#fff",
	};
	const inputStyle = {
		width: "100%", height: 40, borderRadius: 20,
		border: "1px solid #d9d9d9", padding: "0 14px",
		fontSize: 14, outline: "none", boxSizing: "border-box",
	};
	const labelStyle = { fontSize: 13, fontWeight: 500, marginBottom: 8 };
	const fieldStyle = { flex: 1, minWidth: 220 };

	// ── Render ────────────────────────────────────────────────────────────────
	return (
		<PageWrap>
			<TransactionDetailModal
				record={selectedRecord}
				onClose={() => setSelectedRecord(null)}
			/>

			<Row>
				<Col span={24}>
					<FiltersHeader>
						<TotalLog>Total log: {loadingTable ? "…" : totalRecords}</TotalLog>

						<FiltersGroup>

							{/* Search */}
							<SearchInput
								placeholder="Search"
								prefix={<SearchOutlined style={{ color: "#bbb" }} />}
								value={search}
								onChange={(e) => {
									setSearch(e.target.value);
									fetchTransactions(1, { search: e.target.value });
								}}
								allowClear
							/>

							{/* Top-bar dropdowns */}
							<Form form={form} layout="inline" onValuesChange={handleTopFilterChange}>
								<div style={{ display: "flex", gap: 10, alignItems: "center" }}>
									<DropdownWrapper>
										<DropdownCheckBox
											name="actionBy"
											label={loadingFilters ? "Loading…" : "Action by"}
											options={filterOptions.actionBy}
											form={form}
											disabled={loadingFilters}
										/>
									</DropdownWrapper>

									<DropdownWrapper>
										{/* label = mattername  |  value = matterid (sent to API) */}
										<DropdownCheckBox
											name="matter"
											label={loadingFilters ? "Loading…" : "Matter"}
											options={filterOptions.matters}
											form={form}
											disabled={loadingFilters}
										/>
									</DropdownWrapper>

									<DropdownWrapper>
										<DropdownCheckBox
											name="category"
											label={loadingFilters ? "Loading…" : "Category"}
											options={filterOptions.categories}
											form={form}
											disabled={loadingFilters}
										/>
									</DropdownWrapper>
								</div>
							</Form>

							{/* Filter icon with badge */}
							<FilterBtn onClick={() => setFilterModalOpen(true)} title="Advanced Filters">
								<FilterIcon style={{ fontSize: 20 }} />
								{modalFilterCount > 0 && <FilterBadge>{modalFilterCount}</FilterBadge>}
							</FilterBtn>
						</FiltersGroup>
					</FiltersHeader>
				</Col>

				<Col span={24}>
					<StyledTable
						columns={columns}
						dataSource={tableData}
						loading={loadingTable}
						pagination={{
							current: currentPage,
							pageSize: PAGE_SIZE,
							total: totalRecords,
							size: "small",
							onChange: (page) => fetchTransactions(page),
							showSizeChanger: false,
						}}
						scroll={{ x: 800 }}
						size="middle"
					/>
				</Col>
			</Row>

			{/* ── Advanced Filter Modal ── */}
			<Modal
				open={filterModalOpen}
				onCancel={() => setFilterModalOpen(false)}
				footer={null}
				width={900}
				title={`Filter${modalFilterCount > 0 ? ` (${modalFilterCount})` : ""}`}
				centered
			>
				<div style={{ paddingTop: 8 }}>
					<div style={{ display: "flex", gap: 24, marginBottom: 28, flexWrap: "wrap" }}>

						{/* Source */}
						<div style={fieldStyle}>
							<div style={labelStyle}>Source</div>
							<StyledSelect
								mode="multiple"
								allowClear
								placeholder="All"
								value={modalSource}
								onChange={(vals) => setModalSource(vals || [])}
								style={{ width: "100%", borderRadius: 20 }}
								options={filterOptions.sources.map((s) => ({ label: s, value: s }))}
							/>
						</div>

						{/* Destination */}
						<div style={fieldStyle}>
							<div style={labelStyle}>Destination</div>
							<StyledSelect
								mode="multiple"
								allowClear
								placeholder="All"
								value={modalDest}
								onChange={(vals) => setModalDest(vals || [])}
								style={{ width: "100%", borderRadius: 20 }}
								options={filterOptions.destinations.map((d) => ({ label: d, value: d }))}
							/>
						</div>

						{/* Payment Method — hidden on client page */}
						{!clientId && (
							<div style={fieldStyle}>
								<div style={labelStyle}>Payment Method</div>
								<select style={selectStyle} value={modalPayment} onChange={(e) => setModalPayment(e.target.value)}>
									<option value="">All</option>
									{filterOptions.paymentMethods.map((p) => (
										<option key={p} value={p}>{p}</option>
									))}
								</select>
							</div>
						)}

						{/* Currency */}
						<div style={fieldStyle}>
							<div style={labelStyle}>Currency</div>
							<StyledSelect
								mode="multiple"
								allowClear
								placeholder="All"
								value={modalCurrency}
								onChange={(vals) => setModalCurrency(vals || [])}
								style={{ width: "100%", borderRadius: 20 }}
								options={filterOptions.currencies.map((c) => ({
									label: currencyLabelMap[c] || c,
									value: c,
								}))}
							/>
						</div>

						{/* Fund In */}
						<div style={fieldStyle}>
							<div style={labelStyle}>Fund In</div>
							<input
								placeholder="e.g. 200"
								value={modalFundIn}
								onChange={(e) => setModalFundIn(e.target.value)}
								style={inputStyle}
							/>
						</div>

						{/* Fund Out */}
						<div style={fieldStyle}>
							<div style={labelStyle}>Fund Out</div>
							<input
								placeholder="e.g. 200"
								value={modalFundOut}
								onChange={(e) => setModalFundOut(e.target.value)}
								style={inputStyle}
							/>
						</div>

						{/* Fund Balance */}
						<div style={fieldStyle}>
							<div style={labelStyle}>Fund Balance</div>
							<input
								placeholder="e.g. 400"
								value={modalFundBalance}
								onChange={(e) => setModalFundBalance(e.target.value)}
								style={inputStyle}
							/>
						</div>

						{/* Date of Transaction */}
						<div style={fieldStyle}>
							<div style={labelStyle}>Date of Transaction</div>
							<DatePicker.RangePicker
								style={{ width: "32%", borderRadius: 20, height: 40 }}
								format="DD-MM-YYYY"
								allowEmpty={[false, false]}
								value={modalDateFrom && modalDateTo ? [modalDateFrom, modalDateTo] : null}
								onChange={(val) => {
									setModalDateFrom(val ? val[0] : null);
									setModalDateTo(val ? val[1] : null);
								}}
							/>
						</div>

					</div>

					{/* Footer */}
					<div style={{
						display: "flex", justifyContent: "flex-end", gap: 10,
						borderTop: "1px solid #f3f4f6", paddingTop: 16,
					}}>
						<ButtonOutlined onClick={clearModalFilters}>Clear All</ButtonOutlined>
						<ButtonFilled onClick={applyModalFilters}>Apply Filter</ButtonFilled>
					</div>
				</div>
			</Modal>
		</PageWrap>
	);
};

export default Transaction;